/**
 * Business metrics tiles, revenue management, and bottleneck chips.
 */
import { formatCurrency, formatInteger, formatNumber, formatPercent } from "./format.js";
import { detailMetric, escapeAttr, escapeHtml } from "./dashboard-html.js";
import { leadFactorySavedViewVisibilityOptionsHtml } from "./dashboard-saved-views.js";
import type {
  LeadFactoryBusinessBottleneck,
  LeadFactoryBusinessMetrics,
  LeadFactoryRevenueMetrics,
  LeadFactorySavedView
} from "./dashboard-lead-factory-types.js";

export function businessMetricsHtml(metrics: LeadFactoryBusinessMetrics, savedViews: LeadFactorySavedView[]): string {
  return `
    <div class="business-metrics-panel">
      <div class="business-metrics-toolbar">
        <div class="business-metrics-window">
          <span>${formatInteger(metrics.days)} day window</span>
          <div class="segmented-control" role="group" aria-label="Business metrics date window">
            ${[7, 14, 30, 90]
              .map(
                (days) => `
                  <button type="button" class="${metrics.days === days ? "active" : ""}" data-business-metrics-days="${days}">
                    ${days}d
                  </button>
                `
              )
              .join("")}
          </div>
        </div>
        <form class="saved-view-controls" data-business-metrics-saved-view-form>
          <select aria-label="Saved metric view" data-business-metrics-saved-view>
            <option value="">Saved views</option>
            ${savedViews
              .map(
                (view) => `
                  <option value="${escapeAttr(view.id)}" data-saved-view-days="${escapeAttr(view.filters.days)}" ${view.filters.days === metrics.days ? "selected" : ""}>
                    ${escapeHtml(view.name)} (${formatInteger(view.filters.days)}d)
                  </option>
                `
              )
              .join("")}
          </select>
          <button class="button secondary saved-view-share-button" type="button" data-business-metrics-share-view>Copy link</button>
          <button class="button secondary saved-view-share-button" type="button" data-business-metrics-history-view>Manage</button>
          <button class="button secondary saved-view-share-button" type="button" data-business-metrics-rename-view>Rename</button>
          <button class="button secondary saved-view-share-button" type="button" data-business-metrics-delete-view>Delete</button>
          <button class="button secondary saved-view-share-button" type="button" data-business-metrics-grant-view>Grant access</button>
          <button class="button secondary saved-view-share-button" type="button" data-business-metrics-revoke-view>Revoke access</button>
          <select aria-label="Saved view visibility" data-business-metrics-saved-view-visibility>
            ${leadFactorySavedViewVisibilityOptionsHtml()}
          </select>
          <input type="text" maxlength="80" data-business-metrics-saved-view-name value="${escapeAttr(`${metrics.days}d command view`)}" aria-label="Saved view name">
          <button class="button secondary" type="submit">Save view</button>
        </form>
      </div>
      <div class="business-metrics-grid">
        ${businessMetricTile("PDF to Source", formatPercent(metrics.inventory.sourceDocumentCoverageRate), `${formatInteger(metrics.inventory.sourceDocuments)} source docs`)}
        ${businessMetricTile("PDF to Lead", formatPercent(metrics.inventory.leadCoverageRate), `${formatInteger(metrics.inventory.leads)} property leads`)}
        ${businessMetricTile("Contact Ready", formatPercent(metrics.leads.readyToContactRate), `${formatInteger(metrics.leads.readyToContact)} ready`)}
        ${businessMetricTile("Review Decisions", formatInteger(metrics.review.decisions), `${formatInteger(metrics.review.evidenceConfirmed)} confirmed · ${metrics.review.days}d`)}
        ${businessMetricTile("Contacted Leads", formatInteger(metrics.contact.contactedLeads), `${formatInteger(metrics.contact.outreachAttempts)} attempts`)}
        ${businessMetricTile("Active Tasks", formatInteger(metrics.tasks.active), `${formatInteger(metrics.tasks.overdue)} overdue`)}
        ${businessMetricTile("Acquisition Basis", formatCurrency(metrics.revenue.acquisitionBasis), `${formatInteger(metrics.revenue.ownedProperties)} owned properties`)}
        ${businessMetricTile("Forecast Revenue", formatCurrency(metrics.revenue.forecastRevenue), `${formatInteger(metrics.revenue.dispositionRecords)} disposition records`)}
        ${businessMetricTile("Sale Proceeds", formatCurrency(metrics.revenue.saleProceeds), `${formatInteger(metrics.revenue.financeRecords)} finance records`)}
        ${businessMetricTile("Gross Spread", formatCurrency(metrics.revenue.grossSpread), `${formatPercent(metrics.revenue.grossSpreadRate)} on basis`)}
        ${businessMetricTile("Net Margin", formatCurrency(metrics.revenue.netMargin), `${formatPercent(metrics.revenue.netMarginRate)} on basis`)}
        ${businessMetricTile("Avg Hold", `${formatNumber(metrics.revenue.averageHoldDays, 0)}d`, `${formatInteger(metrics.revenue.acquiredInWindow)} acquired in ${metrics.revenue.days}d`)}
      </div>
      ${revenueManagementHtml(metrics.revenue)}
      <div class="business-metrics-columns">
        <div>
          <h3>Bottlenecks</h3>
          <div class="business-bottleneck-list">
            ${metrics.bottlenecks.slice(0, 6).map(bottleneckHtml).join("") || `<p class="empty">No active bottlenecks in this read model.</p>`}
          </div>
        </div>
        <div>
          <h3>Funnel</h3>
          <div class="business-funnel">
            ${countMapChips("Leads", metrics.leads.byStatus)}
            ${countMapChips("Readiness", metrics.leads.byContactReadiness)}
            ${countMapChips("Acquisition", metrics.acquisition.pipelineByStage)}
            ${countMapChips("Owned", metrics.ownedProperties.byStatus)}
          </div>
        </div>
        <div>
          <h3>Task Pressure</h3>
          <div class="business-funnel">
            ${countMapChips("Status", metrics.tasks.byStatus)}
            ${countMapChips("Type", metrics.tasks.byType)}
          </div>
        </div>
      </div>
    </div>
  `;
}

export function revenueManagementHtml(revenue: LeadFactoryRevenueMetrics): string {
  return `
    <section class="revenue-management-panel" data-revenue-management-panel>
      <div class="revenue-management-header">
        <div>
          <h3>Revenue Cohorts</h3>
          <p class="subtle">${formatInteger(revenue.acquiredInWindow)} acquired in the ${formatInteger(revenue.days)} day management window</p>
        </div>
        <div class="revenue-management-summary">
          ${detailMetric("Target", formatCurrency(revenue.targetRevenue))}
          ${detailMetric("Proceeds", formatCurrency(revenue.saleProceeds))}
          ${detailMetric("Expenses", formatCurrency(revenue.totalExpenses))}
          ${detailMetric("Active basis", formatCurrency(revenue.activeBasis))}
          ${detailMetric("Disposed basis", formatCurrency(revenue.disposedBasis))}
        </div>
      </div>
      <div class="revenue-cohort-table-shell">
        <table class="revenue-cohort-table">
          <thead>
            <tr>
              <th>Cohort</th>
              <th>Properties</th>
              <th>Basis</th>
              <th>Target</th>
              <th>Proceeds</th>
              <th>Expenses</th>
              <th>Net</th>
              <th>Hold</th>
            </tr>
          </thead>
          <tbody>
            ${
              revenue.cohorts
                .map(
                  (cohort) => `
                    <tr data-revenue-cohort-row data-revenue-cohort="${escapeAttr(cohort.cohort)}">
                      <td>${escapeHtml(cohort.cohort)}</td>
                      <td>${formatInteger(cohort.properties)}<small>${formatInteger(cohort.disposed)} disposed</small></td>
                      <td>${formatCurrency(cohort.acquisitionBasis)}</td>
                      <td>${formatCurrency(cohort.targetRevenue)}</td>
                      <td>${formatCurrency(cohort.saleProceeds)}</td>
                      <td>${formatCurrency(cohort.totalExpenses)}</td>
                      <td>${formatCurrency(cohort.netMargin)}<small>${formatPercent(cohort.netMarginRate)}</small></td>
                      <td>${formatNumber(cohort.averageHoldDays, 0)}d</td>
                    </tr>
                  `
                )
                .join("") || `<tr><td colspan="8" class="empty">No owned-property cohorts are in this management window.</td></tr>`
            }
          </tbody>
        </table>
      </div>
    </section>
  `;
}

export function businessMetricTile(label: string, value: string, note: string): string {
  return `
    <div class="business-metric-tile">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
      <small>${escapeHtml(note)}</small>
    </div>
  `;
}

export function bottleneckHtml(row: LeadFactoryBusinessBottleneck): string {
  return `
    <div class="business-bottleneck ${escapeAttr(row.severity)}">
      <strong>${escapeHtml(row.label)}</strong>
      <span>${formatInteger(row.count)}</span>
    </div>
  `;
}

export function countMapChips(label: string, counts: Record<string, number>): string {
  const entries = Object.entries(counts)
    .sort(([, left], [, right]) => right - left)
    .slice(0, 5);
  return `
    <div class="business-chip-group">
      <span>${escapeHtml(label)}</span>
      <div>
        ${entries.map(([key, value]) => `<small>${escapeHtml(humanLabel(key))}: ${formatInteger(value)}</small>`).join("") || `<small>none</small>`}
      </div>
    </div>
  `;
}

export function humanLabel(value: string): string {
  return value.replaceAll("_", " ").replaceAll(/\b\w/g, (letter) => letter.toUpperCase());
}

